import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent,
  Stepper, 
  Step, 
  StepLabel,
  useTheme
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import CategoryIcon from '@mui/icons-material/Category';
import FactCheckIcon from '@mui/icons-material/FactCheck';

// Import the FlowChartLoader
import { FlowChartLoader } from '../components/LoadingScreen';

// Context
import { useFileData } from '../context/FileDataContext';

// Mock API service
import { processData } from '../services/dataProcessingService';

const steps = ['Upload File', 'Preview Data', 'Process Data', 'Spend Analysis 🎉'];

// Define exactly 4 processing steps for 10 seconds total (2.5s each)
const processingSteps = [
  { name: 'Researching Supplier', icon: <BusinessIcon /> },
  { name: 'Cleaning Items', icon: <CleaningServicesIcon /> },
  { name: 'Mapping Items', icon: <CategoryIcon /> },
  { name: 'Validating Categorisation', icon: <FactCheckIcon /> }
];

const ProcessingPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { 
    fileData, 
    updateProcessingProgress, 
    setProcessedData, 
    setClassifications,
    completeProcessing 
  } = useFileData();
  const [processingComplete, setProcessingComplete] = useState(false);

  useEffect(() => {
    const simulateProcessing = async () => {
      if (!fileData.file || !fileData.data) {
        // If no file data, redirect back to upload
        navigate('/upload');
        return;
      }

      try {
        // Call the processing service with exactly 4 custom steps
        // Each step will take exactly 2.5 seconds for a total of 10 seconds
        const { processedData, classifications } = await processData(
          fileData.data,
          (progress: number, step: string) => {
            updateProcessingProgress(progress, step);
          },
          fileData.selectedColumns,
          processingSteps.map(step => step.name) // Pass exactly 4 custom step names
        );

        // Update the context with processed data
        setProcessedData(processedData);
        setClassifications(classifications);
        completeProcessing();
        setProcessingComplete(true);

        // Navigate to analysis page after processing is complete
        setTimeout(() => {
          navigate('/analysis');
        }, 1000);
      } catch (error) {
        console.error('Error processing data:', error);
        // Handle error - could redirect to error page or back to upload
      }
    };

    simulateProcessing();
  }, [
    fileData.file,
    fileData.data,
    fileData.selectedColumns,
    navigate,
    updateProcessingProgress,
    setProcessedData,
    setClassifications,
    completeProcessing
  ]);

  // Handle completion of the flow chart loader
  const handleProcessingComplete = () => {
    console.log('Flow chart animation completed');
  };

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Processing Your Data
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          Please wait while we process and classify your data. This will take approximately 10 seconds.
        </Typography>
        <Card 
          elevation={0} 
          sx={{ 
            p: 3, 
            mt: 2,
            bgcolor: 'rgba(142, 44, 76, 0.05)', 
            border: '1px solid rgba(142, 44, 76, 0.1)',
            borderRadius: 3
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 500 }}>
              Your data is being processed and classified. Once complete, you'll be automatically redirected to the analysis page.
            </Typography>
          </Box>
        </Card>
      </Box>
      
      <Stepper 
        activeStep={2} 
        sx={{ 
          mb: 4, 
          '& .MuiStepLabel-root .Mui-completed': {
            color: 'primary.main', 
          },
          '& .MuiStepLabel-root .Mui-active': {
            color: 'primary.main', 
          },
        }}
      >
        {steps.map((label, index) => (
          <Step key={label} completed={index < 2}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      
      <Card 
        elevation={0} 
        sx={{ 
          mb: 3, 
          border: '1px solid #e0e0e0',
          borderRadius: 3,
          overflow: 'hidden',
          p: 0
        }}
      >
        <CardContent sx={{ p: 0, height: '400px', backgroundColor: '#1A1E2E' }}>
          <FlowChartLoader 
            onComplete={handleProcessingComplete}
            horizontalLayout={true}
            showLabels={true}
            primaryColor={theme.palette.primary.main}
            secondaryColor={theme.palette.secondary.main}
            backgroundColor="#1A1E2E"
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default ProcessingPage; 